import { RecoilValue, useRecoilCallback, useSetRecoilState } from "recoil";

import { useBookingsNavigation, usePageContext } from "@ryde/redux";
import {
  ActionsEnum,
  bulkOperationsDrawerAF,
  DrawerType,
  IExtendableDto,
  selectedBookingsSelector,
  selectedCellsBookingsSelector,
  selectedRidesSelector,
  selectedTableRowsIdsAtomFamily,
  selectedTableRowsValues,
  stationsBulkTargetRowsSelector,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";

export const useGenericExportOperation = () => {
  const { entity } = usePageContext();
  const { isCalendarBookingView } = useBookingsNavigation();
  const setBulkOperationsDrawerOpened = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );
  const { openDrawer } = useDrawers();

  const { hasPermissions: hasPermissionAllowExport } = useHasPermissions({
    entity: "system",
    action: ActionsEnum.AllowExport,
  });

  // The selection is only needed when the user actually exports, so read it from the
  // snapshot on click instead of subscribing every bulk drawer to five selectors.
  const openGenericExportDrawer = useRecoilCallback(
    ({ snapshot }) =>
      (): void => {
        const read = <T>(recoilValue: RecoilValue<T>): T =>
          snapshot.getLoadable(recoilValue).getValue();

        const getSelectedRowsData = (): IExtendableDto[] => {
          switch (entity) {
            case "ride":
              return read(selectedRidesSelector);

            case "booking":
              return isCalendarBookingView
                ? read(selectedCellsBookingsSelector)
                : read(selectedBookingsSelector);

            case "station":
              return read(stationsBulkTargetRowsSelector);

            default:
              return read(selectedTableRowsValues(entity));
          }
        };

        const selectedRowsData = getSelectedRowsData();
        const selectedIds = read(selectedTableRowsIdsAtomFamily(entity));

        setBulkOperationsDrawerOpened(false);
        openDrawer({
          drawerProps: {
            type: DrawerType.GenericExport,
            selectedRowsData,
            selectedIds,
            entity,
          },
        });
      },
    [entity, isCalendarBookingView, openDrawer, setBulkOperationsDrawerOpened],
  );

  return { hasPermissionAllowExport, openGenericExportDrawer };
};
